import React, { useState } from "react";
import { Row, Col } from "react-bootstrap";
import RiskMetrics from "../RiskMetrics";
import { formatFloat, formatPercentage } from "../../utils/format";
import InputAmount from "../InputAmount";
import InteractionButton from "../InteractionButton";
import useDeleverage from "../../hooks/useDeleverage";
import useApprove from "../../hooks/useApprove";
import { useToBigNumber, useToTokens } from "../../hooks/useConverter";
import { InteractionModalContainer } from ".";
import useGenericCall from "../../hooks/useGenericCall";
import { useOnchainAccountLendingPool, useOnchainInteractionsLendingPool } from "../../hooks/useOnchain";
import { getWeightedAPR } from "../../utils";
import { ApprovalType, OffchainAccountBorrowable, OffchainBorrowable, OnchainAccountBorrowable, PoolTokenType, offchain, onchain } from "impermax-sdk";


export interface DeleverageInteractionModalProps {
  show: boolean;
  toggleShow(s: boolean): void;
}

export default function DeleverageInteractionModal({show, toggleShow}: DeleverageInteractionModalProps) {
  const [val, setVal] = useState<number>(0);
  const slippage = 1.5; //50%

  const data = useGenericCall(useOnchainAccountLendingPool(), async onchainAccountLendingPool => {
    const changeAmounts = await onchainAccountLendingPool.getInteractionsLendingPool().getDeleverageAmounts(val, slippage);
    const maxDeleverage = await onchainAccountLendingPool.getMaxDeleverage(1);
    const newLeverage = await onchainAccountLendingPool.getLeverage({
      changeBorrowedA: -changeAmounts.bAmountA, 
      changeBorrowedB: -changeAmounts.bAmountB, 
      changeCollateral: -changeAmounts.cAmount,
    });
    const offchainLendingPool = await onchainAccountLendingPool.getLendingPool().getOffchainLendingPool();
    return {
      symbol: await (await onchainAccountLendingPool.getCollateral().getPoolToken().getOffchainPoolToken()).getSymbol(),
      changeAmounts,
      maxDeleverage,
      newLeverage,
      totalAPR: await offchainLendingPool.getTotalAPR() * newLeverage,
      invalidInput: val > maxDeleverage,
      b: await Promise.all([onchainAccountLendingPool.getBorrowableA(), onchainAccountLendingPool.getBorrowableB()].map(
        async onchainAccountBorrowable => {
          const offchainBorrowable = await onchainAccountBorrowable.getPoolToken().getOffchainBorrowable();
          const amount = offchainBorrowable.getPoolTokenType() === PoolTokenType.BorrowableA ? -changeAmounts.bAmountA : -changeAmounts.bAmountB;
          return {
            symbol: await offchainBorrowable.getSymbol(),
            borrowAPR: await offchainBorrowable.getNextBorrowAPR(amount),
            farmingAPR: await offchainBorrowable.getNextFarmingAPR(amount),
            initialBorrowed: await onchainAccountBorrowable.getBorrowed(),
            borrowed: amount + await onchainAccountBorrowable.getBorrowed(),
            borrowedUSD: (amount + await onchainAccountBorrowable.getBorrowed()) * await offchainBorrowable.getTokenPriceAccurate(),
          }
        }
      ))
    }
  }, [val, slippage]);
  
  // Convert to big numbers
  const amountAMin = useToBigNumber(data ? data.changeAmounts.bAmountAMin : null, PoolTokenType.BorrowableA);
  const amountBMin = useToBigNumber(data ? data.changeAmounts.bAmountBMin : null, PoolTokenType.BorrowableB);

  // Convert to big numbers
  const tokens = useToTokens(val);
  const [approvalState, onApprove, permitData] = useApprove(ApprovalType.POOL_TOKEN, tokens, data && data.invalidInput);
  const [deleverageState, deleverage] = useDeleverage(approvalState, data && data.invalidInput, tokens, amountAMin, amountBMin, permitData);
  const onDeleverage = async () => {
    await deleverage();
    setVal(0);
    toggleShow(false);
  }

  if (!data) return null;

  const borrowAPR = getWeightedAPR(data.newLeverage, data.b[0].borrowedUSD, data.b[0].borrowAPR, data.b[1].borrowedUSD, data.b[1].borrowAPR);
  const farmingAPR = getWeightedAPR(data.newLeverage, data.b[0].borrowedUSD, data.b[0].farmingAPR, data.b[1].borrowedUSD, data.b[1].farmingAPR);
  const leveragedAPR = data.totalAPR + farmingAPR - borrowAPR;

  const withdrawAmount = data.changeAmounts.cAmount;
  const repayAmountA = Math.min(data.changeAmounts.bAmountA, data.b[0].initialBorrowed);
  const repayAmountB = Math.min(data.changeAmounts.bAmountB, data.b[1].initialBorrowed);
  const receiveAmountA = data.changeAmounts.bAmountA - repayAmountA;
  const receiveAmountB = data.changeAmounts.bAmountB - repayAmountB;

  return (
    <InteractionModalContainer title="Deleverage" show={show} toggleShow={toggleShow}><>
      <RiskMetrics changeBorrowedA={-data.changeAmounts.bAmountA} changeBorrowedB={-data.changeAmounts.bAmountB} changeCollateral={-data.changeAmounts.cAmount} />
      <InputAmount 
        val={val}
        setVal={setVal}
        suffix={data.symbol}
        maxTitle={'Available'}
        max={data.maxDeleverage}
      />
      <div className="transaction-recap">
        <Row>
          <Col xs={6}>You will withdraw:</Col>
          <Col xs={6} className="text-right">{formatFloat(withdrawAmount)} {data.symbol}</Col>
        </Row>
        <Row>
          <Col xs={6}>You will repay:</Col>
          <Col xs={6} className="text-right">{formatFloat(repayAmountA)} {data.b[0].symbol}</Col>
        </Row>
        <Row>
          <Col xs={6}>You will repay:</Col>
          <Col xs={6} className="text-right">{formatFloat(repayAmountB)} {data.b[1].symbol}</Col>
        </Row>
        {(receiveAmountA > 0 || receiveAmountB > 0) && (<>
          <Row>
            <Col xs={6}>You will receive:</Col>
            <Col xs={6} className="text-right">{formatFloat(receiveAmountA)} {data.b[0].symbol}</Col>
          </Row>
          <Row>
            <Col xs={6}>You will receive:</Col>
            <Col xs={6} className="text-right">{formatFloat(receiveAmountB)} {data.b[1].symbol}</Col>
          </Row>
        </>)}
        <Row>
          <Col xs={6}>Estimated APR:</Col>
          <Col xs={6} className="text-right estimated-apr">{formatPercentage(leveragedAPR)}</Col>
        </Row>
        {(receiveAmountA > 0 || receiveAmountB > 0) && (<Row>
          <Col className="text-center mt-2">
            <b>Notice</b>: you will receive {data.b[0].symbol} and {data.b[1].symbol} in your wallet
          </Col>
        </Row>)}
      </div>
      <Row className="interaction-row">
        <Col xs={6}>
          <InteractionButton name="Approve" onCall={onApprove} state={approvalState} />
        </Col>
        <Col xs={6}>
          <InteractionButton name="Deleverage" onCall={onDeleverage} state={deleverageState} />
        </Col>
      </Row>
    </></InteractionModalContainer>
  );
}